import React from 'react'

import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectProps } from '@mui/material/Select'

type OptionShape = {
  disabled?: boolean
  label: string
  value: any
}

type SelectValueType = string | string[]

type SelectFieldProps = SelectProps<SelectValueType> & {
  clientErrorText?: string
  helperText?: string
  label?: string
  options: OptionShape[]
}

const getRenderValue = (selected: SelectValueType, options: OptionShape[]) => {
  if (Array.isArray(selected)) {
    return selected
      .map((s) => {
        const found = options.find(({ value }) => s === value)
        return found?.label ?? ''
      })
      .join(', ')
  }

  return options.find(({ value }) => selected === value)?.label ?? ''
}

const SelectField: React.FC<SelectFieldProps> = ({
  clientErrorText,
  helperText,
  label,
  options,
  value,
  ...props
}) => {
  const hasError = Boolean(clientErrorText)

  return (
    <FormControl fullWidth>
      {label && (
        <InputLabel error={hasError} htmlFor={props.id} shrink>
          {label}
        </InputLabel>
      )}
      <Select
        error={hasError}
        id={props.id}
        fullWidth
        native={!props.multiple}
        renderValue={(selected: SelectValueType) => {
          if (selected.length === 0) {
            return <em>{props.placeholder || 'Select...'}</em>
          }

          return getRenderValue(selected, options)
        }}
        value={value}
        {...props}
      >
        {options.map((o, idx) =>
          props.multiple ? (
            <MenuItem key={idx} value={o.value}>
              <Checkbox checked={(value?.indexOf?.(o.value) ?? -1) > -1} />
              <ListItemText primary={o.label} />
            </MenuItem>
          ) : (
            <option disabled={o.disabled} key={idx} value={o.value}>
              {o.label}
            </option>
          ),
        )}
      </Select>
      {hasError && <FormHelperText error>{clientErrorText}</FormHelperText>}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

export default SelectField
